import React, { useState } from 'react';
import { 
  Lightbulb, 
  Heart, 
  Pill, 
  Plus, 
  X, 
  Clock,
  CheckCircle2,
  Circle,
  Droplet
} from "lucide-react";

const Recommendations = ({ userData, stepData }) => {
  const [medicationOpen, setMedicationOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ title: '', description: '' });
  const [newMedication, setNewMedication] = useState({
    name: "",
    schedule: "daily",
    time: "08:00",
    taken: false
  });
  const [medications, setMedications] = useState([
    { id: 1, name: "Vitamin D", schedule: "daily", time: "08:00", taken: false },
    { id: 2, name: "Calcium", schedule: "daily", time: "20:00", taken: false }
  ]);

  // Custom toast implementation
  const showCustomToast = (title, description, duration = 3000) => {
    setToastMessage({ title, description });
    setShowToast(true);
    setTimeout(() => setShowToast(false), duration);
  };

  // Generate personalized recommendations based on user and step data
  const generateRecommendations = () => {
    const recommendations = [];
    
    // Low step count recommendation
    if (stepData?.dailyAverage < 5000) {
      recommendations.push({
        icon: <Heart className="h-5 w-5 text-red-500" />,
        text: "Try to increase your daily steps to at least 5,000 for basic heart health benefits."
      });
    }
    
    // Consistency recommendation
    if (stepData?.consistency < 0.7) {
      recommendations.push({
        icon: <Lightbulb className="h-5 w-5 text-yellow-500" />,
        text: "Walking consistently is better than occasional intense sessions. Try to walk every day, even if it's just for 10 minutes."
      });
    }
    
    // Hydration recommendation
    recommendations.push({
      icon: <Droplet className="h-5 w-5 text-blue-500" />,
      text: "Remember to stay hydrated during your walks, especially in warm weather."
    });
    
    // Medication reminder based on user's medication list
    if (medications.length > 0) {
      const upcoming = medications.filter(med => !med.taken);
      if (upcoming.length > 0) {
        recommendations.push({
          icon: <Pill className="h-5 w-5 text-purple-500" />,
          text: `Don't forget to take your ${upcoming.map(med => med.name).join(", ")} as scheduled.`
        });
      }
    }
    
    return recommendations;
  };

  const addMedication = () => {
    if (!newMedication.name) return;
    
    setMedications(prev => [
      ...prev, 
      { 
        ...newMedication, 
        id: Date.now(),
        taken: false 
      }
    ]);
    
    setNewMedication({
      name: "",
      schedule: "daily",
      time: "08:00",
      taken: false
    });
    
    setMedicationOpen(false);
    
    showCustomToast(
      "Medication Added",
      `${newMedication.name} has been added to your reminders.`,
      3000
    );
  };

  const toggleMedication = (id) => {
    setMedications(prev => 
      prev.map(med => 
        med.id === id ? { ...med, taken: !med.taken } : med
      )
    );
  };

  const removeMedication = (id) => {
    setMedications(prev => prev.filter(med => med.id !== id));
  };

  const recommendations = generateRecommendations();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex flex-row items-center justify-between pb-2">
        <h3 className="text-lg font-medium">Health Recommendations</h3>
      </div>
      
      <div className="mt-4">
        <div className="space-y-5">
          {/* Health Recommendations */}
          <div className="space-y-3">
            {recommendations.map((rec, index) => (
              <div key={index} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="mt-1">{rec.icon}</div>
                <p className="text-sm text-gray-600">{rec.text}</p>
              </div>
            ))}
          </div>
          
          {/* Medication Reminders Section */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium flex items-center">
                <Pill className="h-5 w-5 text-purple-500 mr-2" />
                Medication Reminders
              </h3>
              <button 
                onClick={() => setMedicationOpen(true)}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md flex items-center hover:bg-gray-50"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Medication
              </button>
            </div>
            
            {medications.length > 0 ? (
              <div className="space-y-2">
                {medications.map(med => (
                  <div 
                    key={med.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${med.taken ? 'bg-gray-50 border-gray-200' : 'border-purple-100 bg-purple-50'}`}
                  >
                    <div className="flex items-center">
                      <button 
                        onClick={() => toggleMedication(med.id)}
                        className="mr-3 text-purple-500"
                      >
                        {med.taken ? 
                          <CheckCircle2 className="h-5 w-5" /> : 
                          <Circle className="h-5 w-5" />
                        }
                      </button>
                      <div>
                        <p className={`font-medium ${med.taken ? 'text-gray-500 line-through' : ''}`}>
                          {med.name}
                        </p>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {med.time} - {med.schedule}
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => removeMedication(med.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No medication reminders set</p>
            )}
          </div>
        </div>
      </div>

      {/* Modal Dialog for Adding Medication */}
      {medicationOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium">Add Medication Reminder</h3>
              <p className="text-sm text-gray-500">Create a reminder for your medications or supplements</p>
            </div>
            
            <div className="space-y-4">
              {/* Medication Name Input */}
              <div>
                <label htmlFor="medication" className="block text-sm font-medium text-gray-700 mb-1">
                  Medication Name
                </label>
                <input 
                  type="text"
                  id="medication" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Vitamin D, Calcium" 
                  value={newMedication.name}
                  onChange={(e) => setNewMedication({...newMedication, name: e.target.value})}
                />
              </div>
              
              {/* Schedule Select */}
              <div>
                <label htmlFor="schedule" className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule
                </label>
                <select
                  id="schedule"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newMedication.schedule}
                  onChange={(e) => setNewMedication({...newMedication, schedule: e.target.value})}
                >
                  <option value="daily">Daily</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                </select>
              </div>
              
              {/* Time Input */}
              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input 
                  type="time"
                  id="time" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newMedication.time}
                  onChange={(e) => setNewMedication({...newMedication, time: e.target.value})}
                />
              </div>
            </div>
            
            {/* Dialog Footer */}
            <div className="flex justify-end gap-2 mt-6">
              <button 
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setMedicationOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={addMedication}
              >
                Add Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple toast implementation */}
      {showToast && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-xs z-50">
          <div className="font-medium">{toastMessage.title}</div>
          <div className="text-sm text-gray-500">{toastMessage.description}</div>
        </div>
      )}
    </div>
  );
};

export default Recommendations;